if (!customElements.get('lazy-video')) {
  class LazyVideo extends HTMLElement {
    constructor() {
      super();
      this.hydrated = false;
      this.cancelled = false;
      this.shadow = this.attachShadow({ mode: 'open' });

      this.styleEl = document.createElement('style');
      this.styleEl.textContent = `
        :host { display:block }
        .frame { position:relative; width:100%; aspect-ratio:var(--aspect,16/9); overflow:hidden }
        video, .poster, .play-btn { position:absolute; inset:0; width:100%; height:100% }
        video, .poster { object-fit:cover }
        video { z-index:1 }
        .poster { z-index:2 }
        .poster[hidden] { display:none }
        .play-btn {
          z-index:3; display:flex; align-items:center; justify-content:center;
          cursor:pointer; user-select:none;
        }
        .play-btn[hidden] { display:none }
        .play-pill {
          width:6rem; height:6rem;
          display:flex; align-items:center; justify-content:center;
          border-radius:9999px;
          border:3px solid #fff;
          color:#fff;
          background:transparent;
          box-shadow:0 0 10px 4px rgba(0,0,0,0.15),0 0 20px 8px rgba(0,0,0,0.05);
          transition:transform 0.2s;
          transform:rotate(90deg);
          filter:drop-shadow(0 0 0.45rem rgba(0,0,0,0.45));
          pointer-events:none;
        }
        .play-btn:hover .play-pill { transform:rotate(90deg) scale(1.1); }
        .play-pill svg { width:3rem; height:3rem; }
        video.hidden { display:none }
        @media(min-width:768px) {
          video.md\\:block { display:block }
          video.md\\:hidden { display:none }
        }
      `;

      this.frame = document.createElement('div');
      this.frame.className = 'frame';

      this.posterEl = document.createElement('img');
      this.posterEl.className = 'poster';

      this.playBtn = document.createElement('div');
      this.playBtn.className = 'play-btn';
      this.playBtn.innerHTML = '<span class="play-pill"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 80.19" fill="currentColor"><path d="M46.4,1.97c1.67-2.62,5.5-2.62,7.17,0l45.75,71.67c1.81,2.83-.22,6.54-3.58,6.54l-91.48,.02c-3.36,0-5.39-3.71-3.59-6.54L46.4,1.97h0Z"/></svg></span>';

      this.frame.append(this.posterEl, this.playBtn);
      this.shadow.append(this.styleEl, this.frame);

      this.onVisibilityChange = () => (document.hidden ? this.pauseAll() : this.maybeAutoplayVisible());
      this.io = new IntersectionObserver(entries => {
        const entry = entries[0];
        if (!entry) return;
        if (entry.isIntersecting) {
          if (this.shouldAutoplay()) {
            this.hydrateIfNeeded();
            this.maybeAutoplayVisible();
          }
        } else {
          this.pauseAll();
        }
      }, { rootMargin: '200px 0px', threshold: [0, 0.15] });

      this.onPlayClick = () => this.userPlayVisible();
    }

    connectedCallback() {
      this.applyPosterAttributes();
      this.applyLoadingAttributes();
      this.maybePrefetchPoster();
      this.playBtn.hidden = this.shouldAutoplay() || !this.hasAttribute('show-play-btn');
      this.io.observe(this);
      document.addEventListener('visibilitychange', this.onVisibilityChange, { passive: true });
      this.playBtn.addEventListener('click', this.onPlayClick);
    }

    disconnectedCallback() {
      this.io.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.playBtn.removeEventListener('click', this.onPlayClick);
    }

    shouldAutoplay() {
      return (this.getAttribute('autoplay') || '').toLowerCase() === 'true';
    }

    getPrefetchMode() {
      return (this.getAttribute('prefetch') || 'none').toLowerCase();
    }

    applyPosterAttributes() {
      const src = this.getAttribute('poster-src') || '';
      const w = parseInt(this.getAttribute('poster-width') || '', 10);
      const h = parseInt(this.getAttribute('poster-height') || '', 10);
      if (w > 0 && h > 0) this.style.setProperty('--aspect', `${w}/${h}`);
      if (src) this.posterEl.src = src;
      this.posterEl.alt = this.getAttribute('poster-alt') || '';
    }

    applyLoadingAttributes() {
      const loadingAttr = this.getAttribute('poster-loading') || this.getAttribute('loading');
      const fetchPriority = this.getAttribute('poster-fetchpriority') || this.getAttribute('fetchpriority');
      const loading = loadingAttr ? loadingAttr : 'lazy';
      this.posterEl.loading = loading;
      if (fetchPriority) this.posterEl.setAttribute('fetchpriority', fetchPriority);
    }

    maybePrefetchPoster() {
      const pmode = this.getPrefetchMode();
      if (pmode === 'poster' || pmode === 'all') {
        const src = this.posterEl.currentSrc || this.posterEl.src;
        if (src) this.createHeadLinkOnce('preload', 'image', src, this.posterEl.getAttribute('fetchpriority') || null);
      }
    }

    maybePrefetchVideoSources() {
      if (!this.shouldAutoplay()) return;
      const pmode = this.getPrefetchMode();
      if (pmode !== 'video' && pmode !== 'all') return;
      this.videos().forEach(v => {
        const sources = Array.from(v.querySelectorAll('source')).map(s => s.src).filter(Boolean);
        if (v.src) sources.push(v.src);
        sources.forEach(src => this.createHeadLinkOnce('preload', 'video', src, null));
      });
    }

    createHeadLinkOnce(rel, as, href, fetchPriority) {
      const head = document.head || document.getElementsByTagName('head')[0];
      if (!head || !href) return;
      const exists = head.querySelector(`link[rel="${rel}"][as="${as}"][href="${CSS.escape(href)}"]`);
      if (exists) return;
      const link = document.createElement('link');
      link.rel = rel;
      link.as = as;
      link.href = href;
      if (fetchPriority) link.setAttribute('fetchpriority', fetchPriority);
      head.appendChild(link);
    }

    hydrateIfNeeded() {
      if (this.hydrated) return;
      const tpl = this.querySelector('template');
      if (!tpl) return;
      this.frame.appendChild(tpl.content.cloneNode(true));
      tpl.remove();
      this.videos().forEach(v => {
        v.autoplay = false;
        v.preload = 'none';
        v.muted = true;
        v.playsInline = true;
        v.addEventListener('playing', () => { if (!this.cancelled) this.posterEl.hidden = true; });
        v.addEventListener('waiting', () => { this.posterEl.hidden = false; });
        v.addEventListener('stalled', () => { this.posterEl.hidden = false; });
        v.addEventListener('error', () => {
          delete v.dataset.loaded;
          if (!this.cancelled) { this.posterEl.hidden = false; this.playBtn.hidden = false; }
        });
      });
      this.maybePrefetchVideoSources();
      this.hydrated = true;
    }

    videos() {
      return Array.from(this.shadow.querySelectorAll('video'));
    }

    visibleVideos() {
      return this.videos().filter(v => v.offsetParent !== null && v.getClientRects().length > 0);
    }

    maybeAutoplayVisible() {
      if (!this.shouldAutoplay()) return;
      const vids = this.visibleVideos();
      vids.forEach(v => {
        if (!v.dataset.loaded) { v.preload = 'auto'; v.load(); v.dataset.loaded = '1'; }
        const p = v.play();
        if (p && p.catch) p.catch(() => {});
      });
      if (vids.length) { this.posterEl.hidden = true; this.playBtn.hidden = true; }
    }

    userPlayVisible() {
      if (!this.hydrated) this.hydrateIfNeeded();
      const vids = this.visibleVideos();
      if (!vids.length) return;
      this.cancelled = false;
      this.playBtn.hidden = true;
      vids.forEach(v => {
        if (!v.dataset.loaded) { v.preload = 'auto'; v.load(); v.dataset.loaded = '1'; }
        const p = v.play();
        if (p instanceof Promise) {
          p.catch(() => {
            if (!this.cancelled) { this.posterEl.hidden = false; this.playBtn.hidden = false; }
          });
        }
      });
    }

    play() {
      this.hydrateIfNeeded();
      const vids = this.visibleVideos();
      vids.forEach(v => {
        if (!v.dataset.loaded) { v.preload = 'auto'; v.load(); v.dataset.loaded = '1'; }
        const p = v.play();
        if (p && p.catch) p.catch(() => {});
      });
      if (vids.length) { this.posterEl.hidden = true; this.playBtn.hidden = true; }
    }

    pause() {
      this.pauseAll();
    }

    reset() {
      this.cancelled = true;
      this.videos().forEach(v => {
        v.preload = 'none';
        v.load();
        delete v.dataset.loaded;
      });
      this.posterEl.hidden = false;
      if (!this.shouldAutoplay() && this.hasAttribute('show-play-btn')) this.playBtn.hidden = false;
    }

    pauseAll() {
      this.videos().forEach(v => { if (!v.paused) v.pause(); });
      if (!this.shouldAutoplay() && this.hasAttribute('show-play-btn')) this.playBtn.hidden = false;
    }
  }

  customElements.define('lazy-video', LazyVideo);
}
